import { useState } from "react";
import { Loader2, Mic, MessageSquare, MicOff, Square } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LanguagePopover } from "@/pages/agent-interfaces/live-agent/components/LanguagePopover";
import { MoreActionsPopover } from "@/pages/agent-interfaces/live-agent/components/MoreActionsPopover";
import { ctaBorderRadius, resolveCtaColors, type CtaButtonShape } from "@/lib/ctaButtonStyle";
import { DEFAULT_BRANDING } from "@/types/ui/branding";
import type { WidgetButtonStyle, WidgetChatButtonStyle } from "@/types/ui/branding";
import type { ActionButtonStyleOverrides, AddImageSource } from "@/types/voice/voice-session-action-row";

interface StudioActionClusterProps {
  brandColor: string;
  secondaryColor?: string | null;
  isMobile: boolean;
  voiceConnected: boolean;
  isConnecting: boolean;
  onStartVoice: () => void;
  onStartText: () => void;
  onEndConversation: () => void;
  /** Toggles the chat transcript panel from within the connected pill. Omit to hide the chat button. */
  onToggleChat?: () => void;
  chatOpen?: boolean;
  /** Text-only session (voice muted/never engaged): pill drops Mic and the
   *  chat toggle, leaving just Stop + More. */
  isTextOnlyMode?: boolean;
  isMicMuted: boolean;
  onToggleMicMute: () => void;
  isAgentVoiceMuted: boolean;
  onToggleAgentVoice: () => void;
  isPaused?: boolean;
  onTogglePause?: () => void;
  onAddImage?: (source: AddImageSource) => void;
  /** Opens the human-handoff prompt. Omit to hide the "Talk to human" menu item entirely. */
  onTalkToHuman?: () => void;
  /** The tenant's own contact number. Shown as "Call the Genie" only when present. */
  phoneNumber?: string | null;
  onCallGenie?: () => void;
  /** The Genie's provisioned mailbox address. Shown as "Continue over email" only when present. */
  mailboxAddress?: string | null;
  onEmailGenie?: () => void;
  actionButtonStyle?: ActionButtonStyleOverrides | null;
  /** Talk button color/text overrides + shape. */
  ctaButtonStyle?: WidgetButtonStyle | null;
  ctaShape?: CtaButtonShape;
  /** Chat button color/text overrides. */
  chatButtonStyle?: WidgetChatButtonStyle | null;
}

/**
 * Studio's action cluster — mirrors the live-agent page's own control set:
 * idle state shows Talk + Chat entry buttons; connected state shows the same
 * tinted pill (Mic, Stop, More) live-agent's VoiceSessionActionRow renders,
 * with the same More popover items (mute Genie, pause, add photo, languages,
 * talk to human, call the Genie, continue over email).
 */
export function StudioActionCluster({
  brandColor,
  secondaryColor,
  isMobile: _isMobile,
  voiceConnected,
  isConnecting,
  onStartVoice,
  onStartText,
  onEndConversation,
  onToggleChat,
  chatOpen = false,
  isTextOnlyMode = false,
  isMicMuted,
  onToggleMicMute,
  isAgentVoiceMuted,
  onToggleAgentVoice,
  isPaused = false,
  onTogglePause,
  onAddImage,
  onTalkToHuman,
  phoneNumber,
  onCallGenie,
  mailboxAddress,
  onEmailGenie,
  actionButtonStyle,
  ctaButtonStyle,
  ctaShape,
  chatButtonStyle,
}: StudioActionClusterProps) {
  const [showLanguageModal, setShowLanguageModal] = useState(false);

  if (!voiceConnected) {
    // Colors/size are configurable overrides layered on top of the default
    // brand-gradient pill — shape stays the existing rounded-full default
    // unless ctaShape is explicitly set.
    const cta = resolveCtaColors({
      brandColor,
      gradientEnd: secondaryColor || DEFAULT_BRANDING.secondaryColor,
      connected: false,
      gradient: true,
      colorOverride: ctaButtonStyle?.color,
      colorOverride2: ctaButtonStyle?.color2,
      textColorOverride: ctaButtonStyle?.textColor,
    });
    const ctaScale = ctaButtonStyle?.sizeScale ?? 1;
    const chatScale = chatButtonStyle?.sizeScale ?? 1;

    return (
      <div
        className="mt-4 lg:mt-8 flex w-full max-w-sm flex-wrap justify-center gap-3 px-4"
        role="group"
        aria-label="Choose how to start your conversation"
      >
        <Button
          type="button"
          onClick={onStartVoice}
          disabled={isConnecting}
          aria-busy={isConnecting}
          className="min-h-[44px] min-w-[120px] gap-2 rounded-full px-6 font-semibold shadow-lg transition-opacity hover:opacity-90"
          style={{
            background: cta.background,
            color: cta.color,
            borderRadius: ctaShape ? ctaBorderRadius(ctaShape) : undefined,
            ...(ctaScale !== 1 ? { transform: `scale(${ctaScale})` } : {}),
          }}
        >
          {isConnecting ? (
            <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
          ) : (
            <Mic className="h-4 w-4" aria-hidden="true" />
          )}
          {isConnecting ? "Connecting…" : "Talk"}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={onStartText}
          disabled={isConnecting}
          className="min-h-[44px] min-w-[120px] gap-2 rounded-full px-6 font-semibold"
          style={{
            borderColor: chatButtonStyle?.color || undefined,
            color: chatButtonStyle?.color || undefined,
            backgroundColor: chatButtonStyle?.backgroundColor || undefined,
            borderRadius: ctaShape ? ctaBorderRadius(ctaShape) : undefined,
            ...(chatScale !== 1 ? { transform: `scale(${chatScale})` } : {}),
          }}
        >
          <MessageSquare className="h-4 w-4" aria-hidden="true" />
          Chat
        </Button>
      </div>
    );
  }

  // Matches LiveAgentPage's own connected-pill sizing (VoiceSessionActionRow):
  // 36px round buttons, a 36×44 Stop button, 16px/14px icons.
  const pillButtonPx = 36;
  const stopButtonPx = 44;

  return (
    <div className="mt-6 flex w-full justify-center px-3">
      <div
        role="group"
        aria-label="Conversation controls"
        className="flex items-center gap-1.5 rounded-full p-1"
        style={{ background: `${brandColor}0d` }}
      >
        {!isTextOnlyMode && (
          <button
            type="button"
            onClick={onToggleMicMute}
            aria-label={isMicMuted ? "Unmute microphone" : "Mute microphone"}
            aria-pressed={isMicMuted}
            title={isMicMuted ? "Unmute microphone" : "Mute microphone"}
            className="flex items-center justify-center rounded-full transition-opacity hover:opacity-75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-genie-primary focus-visible:ring-offset-2"
            style={{
              width: pillButtonPx,
              height: pillButtonPx,
              color: isMicMuted ? "#FFFFFF" : brandColor,
              background: isMicMuted ? brandColor : "transparent",
            }}
          >
            {isMicMuted ? <MicOff size={16} aria-hidden="true" /> : <Mic size={16} aria-hidden="true" />}
          </button>
        )}
        {!isTextOnlyMode && onToggleChat && (
          <button
            type="button"
            onClick={onToggleChat}
            aria-label={chatOpen ? "Hide chat" : "Show chat"}
            aria-pressed={chatOpen}
            title={chatOpen ? "Hide chat" : "Show chat"}
            className="flex items-center justify-center rounded-full transition-opacity hover:opacity-75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-genie-primary focus-visible:ring-offset-2"
            style={{
              width: pillButtonPx,
              height: pillButtonPx,
              color: chatOpen ? "#FFFFFF" : brandColor,
              background: chatOpen ? brandColor : "transparent",
            }}
          >
            <MessageSquare size={16} aria-hidden="true" />
          </button>
        )}
        <button
          type="button"
          onClick={onEndConversation}
          aria-label="End conversation"
          title="End conversation"
          className="flex items-center justify-center rounded-full transition-opacity hover:opacity-75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-genie-primary focus-visible:ring-offset-2"
          style={{
            width: stopButtonPx,
            height: pillButtonPx,
            color: brandColor,
            background: `linear-gradient(135deg, ${brandColor}20, ${secondaryColor || brandColor}14)`,
          }}
        >
          <Square size={14} fill="currentColor" aria-hidden="true" />
        </button>
        <LanguagePopover
          brandColor={brandColor}
          variant="modal"
          hideTrigger
          open={showLanguageModal}
          onOpenChange={setShowLanguageModal}
        />
        <MoreActionsPopover
          brandColor={brandColor}
          secondaryColor={secondaryColor}
          size={pillButtonPx}
          hideLabel
          onToggleAgentVoice={isTextOnlyMode ? undefined : onToggleAgentVoice}
          isAgentVoiceMuted={isAgentVoiceMuted}
          onTogglePause={onTogglePause}
          isPaused={isPaused}
          onAddImage={onAddImage}
          onOpenLanguages={isTextOnlyMode ? undefined : () => setShowLanguageModal(true)}
          onTalkToHuman={onTalkToHuman}
          onCallGenie={phoneNumber ? onCallGenie : undefined}
          onEmailGenie={mailboxAddress ? onEmailGenie : undefined}
          emailActionLabel="Continue over email"
          actionButtonStyle={{ ...actionButtonStyle, idleBackgroundColor: "transparent" }}
        />
      </div>
    </div>
  );
}

export default StudioActionCluster;
